/**
 * The document: a list of sectors in, one PDF out.
 *
 * Each sheet is two passes over the model. The first learns the shape of the
 * content — which way it turns, how many lots were too small to letter — which
 * is what decides the page and how much room the map gets. The second is built
 * knowing the size the map will actually be drawn at, because every
 * label-fitting decision in the model is measured against that scale: a map
 * fitted smaller than the model assumed would print its house numbers on top of
 * each other.
 */
import {
  buildPlat,
  type PlatData, type PlatFocus, type PlatHousehold,
} from './model';
import { PT_PER_PX, PdfDoc } from '../pdf/pdf';
import {
  contentBox, drawMapPage, drawRosterPages, fitMap, noteHeight, pageSize,
  type UnmappedRow,
  type SheetFlags,
} from './sheet';
import { stampFooters } from '../pdf/page';
export { openBlob, releaseTab, reserveTab } from '../pdf/download';

export const WARD_NAME = 'Lorraine Ward';

export interface SheetSpec {
  key: string;
  title: string;
  focus: PlatFocus;
}

export interface DocumentFlags extends SheetFlags {
  /** The households on each sheet, listed on their own page after the map. */
  showRoster: boolean;
}

export interface BuildArgs {
  plat: PlatData;
  /** Every household in the ward, not just the ones on these sheets. */
  households: PlatHousehold[];
  sheets: SheetSpec[];
  /** Household ids the whole document is limited to; see PlatOptions. */
  limitTo?: ReadonlySet<string>;
  /** What the limit is called, for the document title. */
  limitName?: string;
  flags: DocumentFlags;
  onProgress?: (done: number, total: number, label: string) => void;
}

const byAddress = (a: PlatHousehold, b: PlatHousehold) =>
  a.key.localeCompare(b.key, undefined, { numeric: true })
  // Then by unit, or the twelve doors of one building come out in whatever
  // order the query returned them — they all share a street line now.
  || (a.unit ?? '').localeCompare(b.unit ?? '', undefined, { numeric: true });

export function householdsIn(
  households: PlatHousehold[], focus: PlatFocus, limitTo?: ReadonlySet<string>,
) {
  return households
    .filter((h) => (!limitTo || (h.id != null && limitTo.has(h.id))))
    .filter((h) => (focus.kind === 'ward' ? true
      : focus.kind === 'macro' ? h.macro === focus.name
        : h.micro === focus.name))
    .sort(byAddress);
}

export async function buildSectorPdf(
  { plat, households, sheets, limitTo, limitName, flags, onProgress }: BuildArgs,
): Promise<Blob> {
  const doc = new PdfDoc(
    `${WARD_NAME} — ${limitName ? limitName : 'sector maps'}`);

  for (let i = 0; i < sheets.length; i++) {
    const sheet = sheets[i];
    onProgress?.(i, sheets.length, sheet.title);

    const rows = householdsIn(households, sheet.focus, limitTo);
    const common = {
      data: plat,
      households,
      focus: sheet.focus,
      limitTo,
      showNames: flags.showNames,
    };

    const sheetFlags = flags;

    const probe = buildPlat({ ...common, orientation: 'auto' });
    const { w, h } = pageSize(probe.landscape);
    const box = contentBox(w, h);
    const fit = fitMap(probe.frame, box,
                       noteHeight(probe, sheetFlags, box.x1 - box.x0));
    const px = fit.w / PT_PER_PX;

    const model = buildPlat({
      ...common,
      orientation: probe.landscape ? 'landscape' : 'portrait',
      sheetPx: { portrait: px, landscape: px },
    });

    drawMapPage(doc.addPage(w, h), model, {
      title: sheet.title,
      subtitle: `${rows.length} household${rows.length === 1 ? '' : 's'}`,
      flags: sheetFlags,
    });

    if (flags.showRoster) {
      // The model already worked out which households it could not draw:
      // `orphans` matched no county parcel, `outside` geocode beyond the ward.
      // Both used to be marks or footnotes on the drawing; they are rows here.
      const unmapped: UnmappedRow[] = [
        ...model.orphans.map((h) => ({
          key: h.key, unit: h.unit, label: h.label, why: 'no-parcel' as const })),
        ...model.outside.map((h) => ({
          key: h.key, unit: h.unit, label: h.label, why: 'outside' as const })),
      ].sort((a, b) => a.why.localeCompare(b.why) || a.key.localeCompare(b.key));
      drawRosterPages(doc, sheet.title,
                      rows.map((r) => ({ key: r.key, unit: r.unit, label: r.label })),
                      unmapped);
    }

    // Let the browser paint the progress line before the next sheet.
    await new Promise((r) => setTimeout(r, 0));
  }

  onProgress?.(sheets.length, sheets.length, 'writing');
  stampFooters(doc, WARD_NAME, dateLabel());
  return doc.blob();
}

export const dateLabel = (d = new Date()) =>
  d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * One name for every download, stamped to the minute.
 *
 * The date alone is not enough of a stamp: a second set generated after a
 * change would land in the downloads folder as "(1)", which says nothing
 * about which of the two is the later one.
 */
export function pdfFilename(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    + `-${p(d.getHours())}${p(d.getMinutes())}`;
  return `lorraine-ward-maps-${stamp}.pdf`;
}
